import type { ReactElement } from 'react';
import type { UpdateSettingsBody } from '@contracts/settings';
import { useSettings, useUpdateSettings } from '../api/queries';
import { Broken, Card, Picker, SkeletonRows, Switch, useNotices } from '../ui';
import { SettingsLayout } from './SettingsLayout';

/** When your day in Slack can arrive — the half hours of a working morning. */
const BRIEF_TIMES = ['07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00'];

/**
 * What Ptitim writes to you, and where: the four letters and your day in
 * Slack, each with its own switch.
 *
 * ⚠ A page of its own. The switches existed from 2026-09-20, at the bottom of
 * the Recording card, where nobody looks for mail — "If there is no button
 * about emails in settings - add it, we need to give option for users for not
 * receiveing emails" (Vladimir, 2026-09-24, the day mail went to everybody).
 */
export function Notifications(): ReactElement {
  const settings = useSettings();
  const update = useUpdateSettings();
  const { show } = useNotices();
  const save = (body: UpdateSettingsBody, text: string) =>
    update.mutate(body, {
      onSuccess: () => show({ tone: 'success', text }),
      onError: (e) => show({ tone: 'failure', text: e.message }),
    });

  return (
    <SettingsLayout
      title="Email and Slack"
      sub="what Ptitim writes to you — every letter also carries a link that stops its kind"
    >
      {settings.isPending ? (
        <SkeletonRows rows={4} />
      ) : settings.isError ? (
        <Card flush>
          <Broken what="Your settings" retry={() => void settings.refetch()} />
        </Card>
      ) : (
        <>
          <Card title="Email" flush>
            {/* ⚠ Four switches, all on to begin with, and every message carries
            a link that turns its own kind off without signing in — his
            ruling, 2026-09-20: "On, with one click to stop" (PRD 41). */}
            {(
              [
                [
                  'noteReady',
                  'A meeting is written up',
                  'the note, what was promised, what was decided',
                ],
                ['shared', 'Somebody shares with you', 'a meeting, or a collection you are in'],
                ['weekly', 'Monday summary', 'what you owe and what you are owed'],
                [
                  'beforeMeeting',
                  'Before a meeting',
                  'half an hour before: what is open and who is coming',
                ],
              ] as const
            ).map(([key, label, why]) => (
              <div className="setting" key={key}>
                <span className="k">{label}</span>
                <div className="v">
                  <Switch
                    // ⚠ The switch says its STATE and is NAMED for its setting.
                    // "Written to you" on its own announces nothing to a screen
                    // reader, and there are four of them in a column.
                    aria-label={label}
                    label={settings.data.mail[key] ? 'Written to you' : 'Off'}
                    checked={settings.data.mail[key]}
                    onChange={(e) =>
                      save(
                        { mail: { [key]: e.target.checked } },
                        e.target.checked ? 'On — you will be written to.' : 'Off from now on.',
                      )
                    }
                  />
                  <span className="why">{why}</span>
                </div>
              </div>
            ))}
          </Card>
          {/* Your day, in Slack (PRD 59) — only what is yours to do. */}
          <Card title="Slack" flush>
            <div className="setting">
              <span className="k">Your day in Slack</span>
              <div className="v">
                <span className="cluster" style={{ gap: 8, alignItems: 'center' }}>
                  <Switch
                    aria-label="Your day in Slack"
                    label={settings.data.dailyBrief.on ? 'Every workday' : 'Off'}
                    checked={settings.data.dailyBrief.on}
                    onChange={(e) =>
                      save(
                        { dailyBrief: { on: e.target.checked } },
                        e.target.checked
                          ? 'On — your day arrives in Slack every workday.'
                          : 'Off — no more mornings in Slack.',
                      )
                    }
                  />
                  {settings.data.dailyBrief.on ? (
                    <Picker
                      aria-label="Your day in Slack arrives at"
                      value={settings.data.dailyBrief.at}
                      onChange={(at) => save({ dailyBrief: { at } }, `It arrives at ${at}.`)}
                      width={110}
                      size="sm"
                      options={[...new Set([...BRIEF_TIMES, settings.data.dailyBrief.at])]
                        .sort()
                        .map((t) => ({ value: t, label: t }))}
                    />
                  ) : null}
                </span>
                <span className="why">
                  from the Ptitim app, in your Slack: today’s meetings and what to prepare, and the
                  actions that are yours to do
                  {settings.data.timeZone ? ` — ${settings.data.timeZone} time` : ''}
                </span>
              </div>
            </div>
          </Card>
        </>
      )}
    </SettingsLayout>
  );
}
